import { FC, ReactElement } from "react";
import { Card, TableCell, TableRow, Typography } from "@mui/material";

import CollapsibleTable from "../collapsible-table";
import { columns } from "@/columns/COMMON/import-export";
import { getFormattedValue } from "@/utils";
import { COUNTRIES_TOTAL_FIELDS } from "@/constants";
import { NoData } from "@/components";
import { responsiveFontSizes, spacing } from "@/utils/responsive";

const ImportExportCard: FC<{
  data: {
    exports: IExport[];
    _id: string;
  }[];
  footerData: Record<CountryFields, string | number>;
}> = ({ data, footerData }): ReactElement => {
  const transformData = () => {
    const topLevel: CountryData[] = [];
    const htsData: Record<string, (IHtsNumber & { exportCountry: string })[]> = {};
    const totalUniqueHtsCodes = new Set<string>(); // Track all unique HTS codes across all countries

    if (data.length > 0) {
      data.forEach((country) => {
        const topLevelDistinctSupplierValues = new Set();
        const topLevelDistinctImportValues = new Set();
        const uniqueHtsCodesForCountry = new Set<string>(); // Track unique HTS codes for this country
        let topLevelCustomValue = 0;
        let topLevelCustomDuty = 0;
        let topLevelEffectiveDutyRate = 0;
        let topLevelAvgShipmentValue = 0;
        let topLevelDutySaved = 0;
        let totalHtsCount = 0; // Total count including duplicates

        htsData[country._id] = [];

        // Collect all HTS codes from all export countries
        country.exports.forEach((exportItem) => {
          exportItem.htsNumbers.forEach((hts) => {
            topLevelCustomValue += hts.customValue;
            topLevelCustomDuty += hts.customDuty;
            topLevelEffectiveDutyRate += hts.effectiveDutyRate;
            topLevelAvgShipmentValue += hts.avgShipmentValue;
            topLevelDutySaved += hts.dutySaved;
            hts.distinctSuppliersValues.forEach((supplier: string) =>
              topLevelDistinctSupplierValues.add(supplier)
            );
            hts.distinctImportsValues.forEach((importValue: string) =>
              topLevelDistinctImportValues.add(importValue)
            );
            
            // Track unique HTS codes
            uniqueHtsCodesForCountry.add(hts.id);
            totalUniqueHtsCodes.add(hts.id);
            totalHtsCount++;
            
            // Add HTS with export country info
            htsData[country._id].push({
              ...hts,
              exportCountry: exportItem._id
            });
          });
        });

        topLevelEffectiveDutyRate = totalHtsCount > 0 ? topLevelEffectiveDutyRate / totalHtsCount : 0; // Average of rates

        if (country._id) {
          topLevel.push({
            _id: country._id,
            customValue: topLevelCustomValue,
            customDuty: topLevelCustomDuty,
            distinctImports: topLevelDistinctImportValues.size,
            distinctSuppliers: topLevelDistinctSupplierValues.size,
            effectiveDutyRate: topLevelEffectiveDutyRate,
            avgShipmentValue: topLevelDistinctImportValues.size > 0 ? topLevelCustomValue / topLevelDistinctImportValues.size : 0,
            dutySaved: topLevelDutySaved,
            htsCount: uniqueHtsCodesForCountry.size, // Use unique count
          } as CountryData & { htsCount: number });
        }
      });
    }

    return { topLevel, htsData, totalUniqueHtsCodes: totalUniqueHtsCodes.size };
  };

  const { topLevel, htsData, totalUniqueHtsCodes } = transformData();

  // Show empty state when no data
  if (data.length === 0) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
        <Typography
          color="#030712"
          fontWeight={500}
          sx={{
            fontSize: responsiveFontSizes.xl,
            lineHeight: 1.3,
          }}
        >
          Imports by Country of Origin
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: responsiveFontSizes.xs,
            mb: spacing.md,
          }}
        >
          Expand to see rates by code
        </Typography>
        <NoData />
      </Card>
    );
  }

  return (
    <Card sx={{ p: { xs: 1.5, md: 2 }, height: "100%" }}>
      <Typography
        color="#030712"
        fontWeight={500}
        sx={{
          fontSize: responsiveFontSizes.lg,
          lineHeight: 1.3,
        }}
      >
        Imports by Country of Origin
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontSize: responsiveFontSizes.xs,
          mb: spacing.md,
        }}
      >
        Expand to see rates by code
      </Typography>
      <CollapsibleTable
        columns={columns}
        countries={topLevel}
        htsData={htsData}
        footer={() => (
          <TableRow>
            {COUNTRIES_TOTAL_FIELDS.map((section, index) => {
              const position = columns[index].align;
              let value: string | number = "";
              let key = "";
              if (section.type === "total") {
                key = `${section.name}-${index}`;
                // Special handling for HTS Code(s) column
                if (index === 1) {
                  value = `${totalUniqueHtsCodes} HTS Codes`;
                } else {
                  value = section.name;
                }
              } else {
                key = `${section.fieldName}-${index}`;
                value = getFormattedValue(
                  footerData[section.fieldName],
                  section.symbol ?? "",
                  position
                );
              }
              return (
                <TableCell
                  key={`footer-${key}`}
                  sx={{
                    border: "none",
                    background: "#E6ECED",
                    color: "#030712",
                    borderBottom: "none",
                    fontWeight: "500",
                    fontSize: responsiveFontSizes.base,
                    position: "sticky",
                    bottom: "0",
                    zIndex: "2",
                  }}
                >
                  {value}
                </TableCell>
              );
            })}
          </TableRow>
        )}
      />
    </Card>
  );
};

export default ImportExportCard;
